import { useEffect } from "react";
import React from "react";

import { Modal } from "react-bootstrap";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod/v4";

import { may } from "@/abilities";
import { getFormUtils, hasComponent } from "@/components/CustomerContacts/components";
import SpecificComponent from "@/components/CustomerContacts/SpecificComponent";
import { ScopedForm } from "@/components/ScopedForm";
import { SaveButton } from "@/containers/buttons";
import Loading from "@/containers/Loading";
import RHCheck from "@/containers/RHCheck";
import RHInput from "@/containers/RHInput";
import RHReactSelect from "@/containers/RHReactSelect";
import RHTextarea from "@/containers/RHTextarea";
import TTFormGroup from "@/containers/TTFormGroup";
import type { ContactStatusFragment } from "@/gql/graphql";
import { useAppSelector } from "@/hooks";
import { acdSelector } from "@/store/acdReducer";
import { sessionSelector } from "@/store/sessionReducer";

import type { CallHistoryEntry } from ".";

type PropsType = {
  setCCSchema: (schema: Nullable<z.Schema>) => void;
  callResults: HistoriesCallResultType[];
  states: ContactStatusFragment[];
};

export default function Inner({ setCCSchema, callResults, states }: PropsType) {
  const session = useAppSelector(sessionSelector);
  const acd = useAppSelector(acdSelector);
  const { t } = useTranslation(["translation", "acd", "customerContacts"]);

  const form = useFormContext<CallHistoryEntry>();
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = form;

  const htype = useWatch({ control, name: "htype" });
  const ccDescription: Nilable<string> = useWatch({ control, name: "customerContact.description" as any });
  const direction = useWatch({ control, name: "meta.direction" });
  const reached = useWatch({ control, name: "meta.reached" });

  const values = useWatch({ control });

  useEffect(
    () => setValue("details", ccDescription || ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ccDescription],
  );

  const offerTask =
    may(session, "customer-contacts", "new") &&
    htype === "CALL" &&
    session.currentProject.attrs.cc_in_after_call &&
    hasComponent(session, "form");

  const enforceTask =
    may(session, "customer-contacts", "new") &&
    htype === "CALL" &&
    session.currentProject.attrs.cc_in_after_call &&
    session.currentProject.attrs.enforce_cc_in_after_call &&
    hasComponent(session, "form");

  useEffect(
    () => {
      if (enforceTask) {
        setValue("showCustomerContact", true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(
    () => {
      const getter = getFormUtils(session);

      if (getter && values.showCustomerContact)
        (async () => {
          const { initialValues, validationSchema } = await getter();

          setValue(
            "customerContact",
            initialValues({
              callHistory: form.getValues(),
              customerContact: acd.inactiveCallContact ? { contact: acd.inactiveCallContact } : undefined,

              hideFormActions: true,
              hideContactSubform: true,
              onlyInner: true,
              validStates: states,
            }),
          );

          if (validationSchema) {
            setCCSchema(validationSchema);
          }
        })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.showCustomerContact],
  );

  return (
    <>
      <Modal.Header>
        <Modal.Title>{t("acd:call_history_entry.call_details")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>{t("acd:call_history_entry.summarize_call")}</p>

        <RHInput
          control={control}
          name="subject"
          id="subject"
          label={t("translation:global.subject")}
          namespace="acd"
        />

        {direction === "OUTBOUND" && (
          <TTFormGroup>
            <RHCheck
              control={control}
              type="radio"
              inline
              name="meta.reached"
              id="reached-yes"
              value={true}
              label={t("acd:call_history_entry.reached")}
              namespace="acd"
            />
            <RHCheck
              control={control}
              type="radio"
              inline
              name="meta.reached"
              id="reached-no"
              value={false}
              label={t("acd:call_history_entry.not_reached")}
              namespace="acd"
            />
          </TTFormGroup>
        )}

        {!!callResults.length && (
          <RHReactSelect
            control={control}
            id="callResultId"
            name="callResultId"
            label="Gesprächsergebnis"
            namespace="acd"
            options={callResults.map((result) => ({
              label: result.name,
              value: result.id,
            }))}
          />
        )}

        {offerTask && !enforceTask && (
          <TTFormGroup>
            <RHCheck
              control={control}
              name="showCustomerContact"
              id="showCustomerContact"
              label={t("acd:call_history_entry.create_customer_contact")}
              namespace="acd"
            />
          </TTFormGroup>
        )}

        {!values.showCustomerContact && reached !== false && (
          <RHTextarea
            control={control}
            name="details"
            id="details"
            label={t("translation:global.details")}
            namespace="acd"
          />
        )}

        {values.showCustomerContact && !!values.customerContact && (
          <React.Suspense fallback={<Loading />}>
            <ScopedForm prefix="customerContact">
              <SpecificComponent
                name="form"
                callHistory={form.getValues()}
                customerContact={acd.inactiveCallContact ? { contact: acd.inactiveCallContact } : undefined}
                hideFormActions
                hideContactSubform
                onlyInner
                validStates={states}
              />
            </ScopedForm>
          </React.Suspense>
        )}
      </Modal.Body>

      <Modal.Footer>
        <SaveButton type="submit" disabled={isSubmitting}>
          {t("translation:global.save")}
        </SaveButton>
      </Modal.Footer>
    </>
  );
}
